import { useQuery, useMutation } from "@tanstack/react-query";
import { hub } from "@/lib/hub-client";

export interface CatalogOutcome {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  price_tokens: number;
  price_kind: "fixed" | "variable";
  max_price_tokens: number | null;
  delivery_mode: "sync" | "async";
  rating_avg: number;
  rating_count: number;
  partner_slug: string;
  partner_name: string;
}

export interface CatalogPage {
  items: CatalogOutcome[];
  next_cursor: string | null;
}

export function useCatalog(filters: { partner?: string; category?: string } = {}) {
  return useQuery({
    queryKey: ["catalog", filters],
    queryFn: async () => {
      const params: Record<string, string> = { limit: "20" };
      if (filters.partner) params.partner = filters.partner;
      if (filters.category) params.category = filters.category;
      const res = await hub.GET<CatalogPage>("/v1/catalog", { params });
      if (!res.data) throw new Error("catalog fetch failed");
      return res.data;
    },
  });
}

export interface OutcomeDetail {
  outcome_id: string;
  slug: string;
  name: string;
  description: string | null;
  price_tokens: number;
  price_kind: "fixed" | "variable";
  max_price_tokens: number | null;
  price_formula: string | null;
  delivery_mode: "sync" | "async";
  assets_managed_by: "appsumo" | "partner";
  max_duration_seconds: number;
  shareable: "opt-in" | "never";
  rating_avg: number;
  rating_count: number;
  partner_slug: string;
  partner_name: string;
  version_id: string;
  version_num: number;
  input_schema: Record<string, unknown>;
  output_schema: Record<string, unknown>;
}

export function useOutcome(partnerSlug: string, outcomeSlug: string) {
  return useQuery({
    queryKey: ["outcome", partnerSlug, outcomeSlug],
    queryFn: async () => {
      const res = await hub.GET<OutcomeDetail>(
        `/v1/catalog/outcomes/${partnerSlug}/${outcomeSlug}`,
      );
      if (res.response.status === 404) return null;
      if (!res.data) throw new Error("outcome fetch failed");
      return res.data;
    },
    enabled: Boolean(partnerSlug && outcomeSlug),
  });
}

export interface CreateJobResult {
  job_id: string;
  status: "holding" | "running";
  price_tokens: number;
  max_price_tokens: number;
  deadline_at: string;
}

export function useCreateJob() {
  return useMutation({
    mutationFn: async (input: {
      outcome_id: string;
      input: Record<string, unknown>;
    }): Promise<CreateJobResult> => {
      const res = await hub.POST<CreateJobResult>("/v1/jobs", { body: input });
      if (!res.data) {
        const text = await res.response.text().catch(() => "");
        throw new Error(`create job failed: ${res.response.status} ${text.slice(0, 200)}`);
      }
      return res.data;
    },
  });
}
